import {
  ApplicationRef,
  ComponentFactoryResolver,
  Injectable,
  Injector,
  EmbeddedViewRef,
  Type,
} from '@angular/core';
import { fromEvent } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { CustomModalRef } from './modal-ref';
import { CustomModalConfig } from './modal-config';

@Injectable({ providedIn: 'root' })
export class CustomModalService {
  closeButton = true;

  constructor(
    private appRef: ApplicationRef,
    private resolver: ComponentFactoryResolver,
    private injector: Injector
  ) {}

  /**
   * Open a component as a modal dialog.
   * @param component Component class to render inside the modal.
   * @param config Optional modal configuration object.
   */
  open<T, D = any, R = any>(
    component: Type<T>,
    config: CustomModalConfig<D> = {}
  ): CustomModalRef<R> {
    // Create a ModalRef – it keeps handles for cleanup and communication
    const backdropHost = document.createElement('div');
    const portalHost = document.createElement('div');

    if (config.fullscreen) {
      portalHost.style.position = 'fixed';
      portalHost.style.inset = '0';
      portalHost.style.width = '100vw';
      portalHost.style.height = '100vh';
      this.closeButton = false;
    } else {
      if (config.width) portalHost.style.width = config.width;
      if (config.height) portalHost.style.height = config.height;
      if (config.maxWidth) portalHost.style.maxWidth = config.maxWidth;
      if (config.maxHeight) portalHost.style.maxHeight = config.maxHeight;
      this.closeButton = true;
    }

    // Apply CSS classes for styling & sizing
    backdropHost.className = `custom-modal-backdrop ${
      config.backdropClass ?? ''
    }`.trim();
    portalHost.className = `custom-modal-panel ${
      config.panelClass ?? ''
    }`.trim();

    // Append to DOM (backdrop then panel for stacking context)
    document.body.appendChild(backdropHost);
    document.body.appendChild(portalHost);

    // Create container that hosts user's component inside (for margin/padding/focus-trap etc.)
    const containerFactory = this.resolver.resolveComponentFactory(
      CustomModalContainerComponent
    );
    const containerRef = containerFactory.create(this.injector);
    this.appRef.attachView(containerRef.hostView);
    (containerRef.hostView as EmbeddedViewRef<any>).rootNodes.forEach((node) =>
      portalHost.appendChild(node)
    );

    // Pass config & close ref to the container (similar to MAT_DIALOG_DATA)
    containerRef.instance.config = config;

    const modalRef = new CustomModalRef<R>(
      portalHost,
      backdropHost,
      !!config.disableClose
    );
    containerRef.instance.modalRef = modalRef;

    // Create user's component dynamically inside the container
    const userCmpFactory = this.resolver.resolveComponentFactory(component);
    const userCmpRef = userCmpFactory.create(
      this.createChildInjector(config.data, modalRef)
    );
    containerRef.instance.attachComponentPortal(userCmpRef.hostView);

    // Save/lock scroll
    modalRef._saveOverflow();

    // Backdrop click handling (if allowed)
    if (!config.disableClose) {
      fromEvent(backdropHost, 'click')
        .pipe(take(1))
        .subscribe(() => modalRef.close());
    }

    // ESC key close (if allowed)
    if (!config.disableClose) {
      fromEvent<KeyboardEvent>(window, 'keydown')
        .pipe(
          filter((event) => event.key === 'Escape'),
          take(1)
        )
        .subscribe(() => modalRef.close());
    }

    return modalRef;
  }

  // --------------------- //
  private createChildInjector<D, R>(
    data: D | undefined,
    modalRef: CustomModalRef<R>
  ): Injector {
    return Injector.create({
      providers: [
        { provide: CUSTOM_MODAL_DATA, useValue: data },
        { provide: CustomModalRef, useValue: modalRef },
        // --- aliasy dla kompatybilności z kodem Material ---
        { provide: MAT_DIALOG_DATA, useValue: data },
        { provide: MatDialogRef, useValue: modalRef },
      ],
      parent: this.injector,
    });
  }
}

// Injection token for modal data
import { InjectionToken } from '@angular/core';
import { CustomModalContainerComponent } from './custom-modal-container.component';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
export const CUSTOM_MODAL_DATA = new InjectionToken<any>('CUSTOM_MODAL_DATA');
